import styled from 'styled-components'
import { signInForm } from '../../containers/SignInForm/styles'
import { signUpFields } from '../../containers/SignUpForms/styles'
import { withOpacity } from '../../helpers'
import { authCard, authHeader, authScreen } from '../auth-card'

const brushStroke = (color: string): string => {
  const paths = [
    'M1 7.4L5 5.2C50 4.5 100 4.1 150 4.3C210 4.5 270 3.8 336 2.9L337.5 4.5L334 5.6'
      + 'C290 6.3 240 6.7 190 7C130 7.3 70 7.8 3.5 8.6Z',
    'M18 9.1C80 8.5 140 8.2 205 8.1L204 8.7C140 8.9 80 9.3 20 9.8Z',
    'M58 3.7C120 3.2 200 2.8 296 2.3L297 2.8C200 3.4 120 3.8 59 4.2Z',
    'M236 7.2C270 6.9 300 6.6 330 6.1L329 6.6C300 7.1 270 7.5 237 7.8Z',
  ]
  const markup = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 338 10" preserveAspectRatio="none">'
    + paths.map(path => `<path d="${ path }" fill="${ color }"/>`).join('')
    + '</svg>'

  return `url("data:image/svg+xml,${ encodeURIComponent(markup) }")`
}

export const StyledLandingArticle = styled.article<{ $heroImage: string }>`
  width: 100%;
  background-color: ${ ({ theme }) => theme.colors.white };
  color: ${ ({ theme }) => theme.colors.landingDark };
  font-family: ${ ({ theme }) => theme.fontFamily };

  main {
    display: grid;
    grid-template-columns: minmax(0, 58fr) minmax(0, 42fr);
    min-height: 100vh;
  }

  .hero {
    display: flex;
    flex-direction: column;
    padding: 32px 8% 80px;
    box-sizing: border-box;
    color: ${ ({ theme }) => theme.colors.white };
    background-color: ${ ({ theme }) => theme.colors.greyLight };
    background-image:
      linear-gradient(
        ${ ({ theme }) => withOpacity(theme.colors.landingDark, 18) },
        ${ ({ theme }) => withOpacity(theme.colors.landingDark, 18) }
      ),
      linear-gradient(
        180deg,
        ${ ({ theme }) => withOpacity(theme.colors.landingHero, 73) } 0%,
        ${ ({ theme }) => withOpacity(theme.colors.landingHero, 54) } 10.73%,
        ${ ({ theme }) => withOpacity(theme.colors.landingHero, 45) } 20.58%,
        ${ ({ theme }) => withOpacity(theme.colors.landingPrimary, 35) } 33.22%,
        ${ ({ theme }) => withOpacity(theme.colors.landingDark, 49) } 81.9%
      ),
      url(${ ({ $heroImage }) => $heroImage });
    background-size: 100% 100%, 100% 100%, auto 118%;
    background-position: 0 0, 0 0, 78% 0;
    background-repeat: no-repeat;
  }

  .hero-header {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .hero-logo {
    width: 120px;
    height: auto;
  }

  .hero-content {
    margin: auto 0;
    padding-top: 48px;
    max-width: 560px;
    text-shadow: 0 1px 6px ${ ({ theme }) => withOpacity(theme.colors.landingDark, 40) };
  }

  h1 {
    margin: 0 0 24px;
    font-size: 44px;
    font-weight: bold;
    line-height: 1.2;
  }

  h1 .highlight {
    background-image: ${ ({ theme }) => brushStroke(theme.colors.landingHighlight) };
    background-size: 100% 0.26em;
    background-position: 0 100%;
    background-repeat: no-repeat;
    padding-bottom: 0.12em;
  }

  .subtitle {
    margin: 0 0 56px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1.5;
  }

  .hero-download {
    text-shadow: none;

    p {
      text-shadow: 0 1px 6px ${ ({ theme }) => withOpacity(theme.colors.landingDark, 40) };
    }
  }

  .access {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 48px 24px;
    box-sizing: border-box;
  }

  .hero-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 32px;
  }

  .language-selector {
    .option {
      color: ${ ({ theme }) => theme.colors.landingDark };
    }

    .option .code {
      color: ${ ({ theme }) => theme.colors.landingPrimary };
    }

    .option.selected {
      background-color: ${ ({ theme }) => withOpacity(theme.colors.landingHighlight, 20) };
    }

    .option:focus-visible {
      outline-color: ${ ({ theme }) => theme.colors.landingPrimaryDark };
    }
  }

  form {
    width: 100%;
    max-width: 336px;
    display: flex;
    flex-direction: column;

    header {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 32px;
      text-align: center;

      img {
        width: 96px;
        height: 96px;
        padding: 4px;
        border-radius: 24px;
        background-color: ${ ({ theme }) => theme.colors.white };
        box-shadow:
          0 4px 16px ${ ({ theme }) => withOpacity(theme.colors.landingDark, 12) },
          0 4px 0 ${ ({ theme }) => withOpacity(theme.colors.landingHighlight, 25) };
      }

      h2 {
        margin: 24px 0 0;
        font-size: 20px;
        font-weight: bold;
        color: ${ ({ theme }) => theme.colors.landingPrimary };
      }
    }

    ${ signInForm }
    ${ signUpFields }

    .backButton {
      width: 20px;
      height: 20px;
      margin: 0 0 20px 0;
      display: block;
      cursor: pointer;
      align-self: flex-start;
    }

    .actions .link {
      margin-top: 30px;
    }

    .spinner-container {
      flex: 1;
    }

    .fields .phone-code-container {
      top: 0;
      bottom: 0;
      align-items: center;
      gap: 6px;

      .flag-container {
        font-size: 20px;
        line-height: 1;
      }

      span {
        font-size: 16px;
        line-height: 1;
        color: ${ ({ theme }) => theme.colors.landingDark };
      }
    }

    .fields > div:not(.filled) label {
      top: 11px;
      font-size: 16px;
    }

    .fields .phone-input {
      padding-left: 76px;
    }

    .fields > div:not(.invalid) {
      label {
        color: ${ ({ theme }) => withOpacity(theme.colors.landingDark, 70) };
      }

      input {
        border-bottom-color: ${ ({ theme }) => theme.colors.landingPrimary };
        color: ${ ({ theme }) => theme.colors.landingDark };
      }
    }

    .link span,
    .actions .secondary-actions span,
    .actions .verification {
      color: ${ ({ theme }) => theme.colors.landingPrimaryDark };
    }

    .fields label {
      font-weight: 400;
    }

    .forgotten-password span {
      font-size: 13px;
      font-weight: 400;
    }

    .actions .verification {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;

      .go {
        font-weight: 700;
      }
    }

    .actions .divider {
      color: ${ ({ theme }) => theme.colors.landingDark };
      font-size: 12px;
      font-weight: 700;

      &::before,
      &::after {
        background-color: ${ ({ theme }) => withOpacity(theme.colors.landingPrimary, 40) };
      }
    }

    .actions .secondary-actions span {
      font-weight: 700;
      letter-spacing: 1px;
    }

    .actions button.filled {
      font-weight: 700;
      letter-spacing: 1.5px;
      color: ${ ({ theme }) => theme.colors.landingDark };
      background-color: ${ ({ theme }) => theme.colors.landingHighlight };

      &:hover {
        background-color: ${ ({ theme }) => withOpacity(theme.colors.landingHighlight, 80) };
      }

      &:disabled {
        color: ${ ({ theme }) => withOpacity(theme.colors.landingDark, 60) };
        background-color: ${ ({ theme }) => withOpacity(theme.colors.landingHighlight, 40) };
      }

      &:focus-visible {
        outline: 2px solid ${ ({ theme }) => theme.colors.landingDark };
        outline-offset: 2px;
      }
    }
  }

  .access-download {
    display: none;
  }

  @media screen and (max-width: 768px) {
    main {
      grid-template-columns: minmax(0, 1fr);
      min-height: 0;
    }

    .hero {
      min-height: 380px;
      padding: 20px 16px 40px;
      background-size: 100% 100%, 100% 100%, cover;
      background-position: 0 0, 0 0, 70% bottom;
    }

    .hero-logo {
      width: 104px;
    }

    .hero-content {
      margin: 0;
      padding-top: 40px;
    }

    h1 {
      margin-bottom: 16px;
      font-size: 32px;
    }

    .subtitle {
      margin: 0;
      font-size: 14px;
    }

    .hero-download {
      display: none;
    }

    .access {
      padding: 32px 16px 48px;
    }

    .hero-actions {
      gap: 12px;
    }

    .language-selector {
      order: -1;
    }

    form header {
      align-items: flex-start;
      text-align: left;

      img {
        display: none;
      }

      h2 {
        margin-top: 0;
      }
    }

    .access-download {
      display: block;
      margin-top: 40px;
      text-align: center;

      ul {
        justify-content: center;
        gap: 16px;
      }
    }
  }
`

export default styled.article`
  ${ authScreen }

  form {
    ${ authCard }

    ${ authHeader }

    .federated {
      display: flex;
      flex-direction: column;
    }

    ${ signInForm }
  }
`

// The pre-redesign sign in (see appsSignInLayout): wide card, logo, underlined title, centred
// underlined links and the sign up link under the button.
export const StyledClassicArticle = styled.article`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 25px;
  box-sizing: border-box;

  form {
    ${ signInForm }

    width: 515px;
    max-width: 100%;
    border-radius: 20px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    padding: 70px 90px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    background-color: ${ ({ theme }) => theme.colors.white };

    header {
      flex: 0;
      margin-bottom: 25px;
      text-align: center;

      img.logoAmb {
        max-width: 365px;
        width: 100%;
      }

      img.logoNemiRounded,
      img.logoBeiraBaixaRounded {
        max-width: 100px;
      }
    }

    .link span {
      text-decoration: underline;
    }

    .forgotten-password {
      text-align: center;
      margin-top: 0;

      span {
        font-size: 16px;
        font-weight: 500;
      }
    }

    .actions {
      margin-top: 20px;
      padding: 5px;

      .sign-up {
        margin-top: 30px;
      }
    }

    @media screen and (max-width: 768px) {
      padding: 40px 24px;
    }
  }
`
